import { ArrowUpRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { CALENDLY_URL } from "@/constants/site";

const TIERS = [
  {
    tag: "Tier 01 — Diagnostic Sprint",
    title: "The Sprint",
    audience: "For founding teams that need assets, not headcount.",
    includes: [
      "Architecture documents, executive briefs, and whitepaper blueprints",
      "5\u201315 minute technical screen-flows and explainers",
      "Domain-tailored value narratives for conservative verticals",
      "Targeted validation of a specific architecture or pursuit blocker",
    ],
    ownership: "TECC designs and produces the assets; your team runs the motion.",
    cadence: "A finite 2\u20136 week sprint. Then done.",
    testId: "offering-tier-sprint",
  },
  {
    tag: "Tier 02 — Retained Counsel",
    title: "Retained Field CTO Counsel",
    audience: "For platforms in active enterprise pursuits.",
    includes: [
      "Must-win Global 2000 rooms \u2014 architecture, security & compliance validation",
      "SKU mapping and product-to-buyer alignment at board level",
      "Selective ecosystem introductions where there is genuine fit",
      "Named-pursuit technical blockage clearing",
    ],
    ownership: "Scarce days per month. Your team carries the cadence; I sit the rooms that close.",
    cadence: "A scarce monthly retainer. Limited concurrent engagements.",
    testId: "offering-tier-retained",
  },
  {
    tag: "Tier 03 — Critical-Path Intensive",
    title: "The Intensive",
    audience: "For a defined set of must-win pursuits, or a first enterprise motion.",
    includes: [
      "Time-boxed concentration on named, must-win pursuits",
      "Deal-room validation across architecture, security, and compliance",
      "Sprint assets produced in parallel where they unblock a pursuit",
    ],
    ownership: "Intense but finite \u2014 then back to retained counsel, or done.",
    cadence: "Short and defined. Not a second job, for either of us.",
    testId: "offering-tier-intensive",
  },
];

const NOT_LIST = [
  "Not a full-time Field CTO or Head of SE",
  "Not on-call pre-sales or SLA-bound pipeline coverage",
  "Not a body shop or fractional IC",
  "Not unlimited pursuit support",
];

export default function Offerings() {
  return (
    <section
      id="offerings"
      data-testid="offerings-section"
      className="px-6 lg:px-12 py-24 lg:py-32 border-t border-white/10"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-6">
            03 — Offerings
          </p>
          <h2 className="font-serif tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl leading-[1.1]">
            Light-Touch Retainers &{" "}
            <span className="italic text-[#E6E4D9]">Short Sprints</span>
          </h2>
          <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white/60">
            Not hourly. Not headcount. A fraction of a full-time senior Field
            CTO — investment is scoped after a briefing and confirmed in a
            written proposal.
          </p>
        </Reveal>

        <div className="mt-14 lg:mt-20 grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {TIERS.map((tier, i) => (
            <Reveal key={tier.tag} delay={i * 0.1} className="bg-[#0A0A0A]">
              <article
                data-testid={tier.testId}
                className="h-full flex flex-col p-10 lg:p-12 hover:bg-[#101010] transition-colors duration-500"
              >
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9] mb-5">
                  {tier.tag}
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight mb-4">
                  {tier.title}
                </h3>
                <p className="text-sm font-light text-white/50 mb-8">
                  {tier.audience}
                </p>
                <ul className="space-y-4 flex-1">
                  {tier.includes.map((item, j) => (
                    <li
                      key={j}
                      className="flex gap-3 text-sm font-light leading-relaxed text-white/70"
                    >
                      <span className="mt-[0.6rem] h-px w-4 shrink-0 bg-[#E6E4D9]/50" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 pt-6 border-t border-white/10 space-y-3">
                  <p className="text-sm font-light leading-relaxed text-white/60">
                    {tier.ownership}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {tier.cadence}
                  </p>
                </div>
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${tier.testId}-cta`}
                  className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white hover:text-[#E6E4D9] transition-colors duration-300"
                >
                  Schedule Executive Briefing
                  <ArrowUpRight
                    size={13}
                    weight="bold"
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            data-testid="offerings-not-block"
            className="mt-10 border border-white/10 px-6 lg:px-10 py-8"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-6">
              This is not
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
              {NOT_LIST.map((item) => (
                <p
                  key={item}
                  className="flex gap-3 text-sm font-light text-white/60"
                >
                  <span className="text-[#E6E4D9]/70 select-none">—</span>
                  {item}
                </p>
              ))}
            </div>
            <p className="mt-8 text-sm font-light leading-relaxed text-white/50 max-w-3xl">
              Work is scoped in sprints and scarce retainers. Deliverables are
              assets and decisions, not hours. No standing presales overhead.
              Any longer-term alignment structure is discussed in briefing.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
